import { cx } from "@emotion/css";
import { Plus } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import type { FolderItemType } from "src/components/Folders/types";
import { DeckCard } from "src/components/NewComponents/Search/DecksViews/DeckCard";
import { FolderItemSkeleton } from "src/components/NewComponents/Skeletons/FolderItemSkeleton";
import { type SearchDeck, useGetUserDecks } from "src/hooks/useFlashcards";

interface ModalSelectedItem {
	itemId: string;
	itemType: FolderItemType;
}

interface DeckItem {
	id: string;
	name?: string;
	[key: string]: unknown;
}

interface FlashcardsFolderTabProps {
	debouncedSearchValue: string;
	selectedItems: ModalSelectedItem[];
	toggleSelection: (item: DeckItem, type: FolderItemType) => void;
	onClose: () => void;
	setIsSearching: (value: boolean) => void;
	folderId: string;
}

export const FlashcardsFolderTab = ({
	debouncedSearchValue,
	selectedItems,
	toggleSelection,
	onClose,
	setIsSearching,
	folderId,
}: FlashcardsFolderTabProps) => {
	const { t } = useTranslation();
	const history = useHistory();

	const handleCreate = () => {
		onClose();
		history.push(`${routing.paths.createDeck}?folderId=${folderId}`);
	};

	const {
		data: decks,
		isFetching: isFetchingDecksData,
		isLoading: isFetchingDecks,
	} = useGetUserDecks();

	useEffect(() => {
		setIsSearching(isFetchingDecksData && !isFetchingDecks);
	}, [isFetchingDecksData, isFetchingDecks, setIsSearching]);

	const flatDecks = useMemo(() => {
		if (!decks) return [];
		if (!debouncedSearchValue) return decks;
		return (decks as SearchDeck[]).filter((deck) =>
			deck.name?.toLowerCase().includes(debouncedSearchValue.toLowerCase()),
		);
	}, [decks, debouncedSearchValue]);

	const renderDeckItem = (item: SearchDeck) => {
		const isSelected = selectedItems.some(
			(i) => i.itemId === String(item.id) && i.itemType === "deck",
		);

		return (
			<div
				key={item.id}
				data-test-id="folder-modal-deck-item"
				data-deck-id={item.id}
				onClick={() =>
					toggleSelection({ ...item, id: String(item.id) }, "deck")
				}
				className={cx("cursor-pointer position-relative transition-all h-100")}
			>
				<DeckCard
					deck={item}
					hidePreview
					className={cx(
						"pointer-events-none mb-0 w-100 mx-0 h-100",
						isSelected ? "border-color-primary" : "",
					)}
				/>
			</div>
		);
	};

	if (isFetchingDecks && !flatDecks.length) {
		return (
			<div className="folder-modal-scroll">
				<div className="folder-modal-grid">
					{Array.from({ length: 4 }).map((_, i) => (
						<FolderItemSkeleton key={i} />
					))}
				</div>
			</div>
		);
	}

	if (!flatDecks.length) {
		return (
			<div className="flex flex-col items-center justify-center py-48 gap-16">
				<span className="text-black-60 font-14">
					{debouncedSearchValue
						? t("folder.noSearchResults", "Brak wyników wyszukiwania")
						: t("folder.noDecks", "Nie masz jeszcze żadnych zestawów fiszek")}
				</span>
				{!debouncedSearchValue && (
					<PrimaryButton
						onClick={handleCreate}
						className="flex items-center gap-8"
					>
						<Plus size={16} />
						{t("folder.createDeck", "Utwórz zestaw fiszek")}
					</PrimaryButton>
				)}
			</div>
		);
	}

	return (
		<div className="folder-modal-scroll">
			<div className="folder-modal-grid">{flatDecks.map(renderDeckItem)}</div>
		</div>
	);
};
